/**
 * "What you could use" — Section B on /prompts.
 *
 * Templates installed in the workspace but not assigned to the current user.
 * Each tile carries a "Request access" CTA that opens the parent's
 * RequestModal pre-keyed to the template name.
 *
 * Per Ean 2026-06-10 card-density: collapse to 6 tiles with Show-all toggle.
 */

import { useState } from 'react';

import type { MyKnowledgeCatalogRow } from '../../lib/api.ts';
import { EmptyState } from '../../components/EmptyState.tsx';
import { PromptCategoryBadge } from '../../components/PromptCategoryBadge.tsx';

interface AvailablePromptsSectionProps {
  available: MyKnowledgeCatalogRow[];
  /** Opens the parent's RequestModal pre-keyed to the template slug. */
  onRequest: (slug: string) => void;
}

export function AvailablePromptsSection({ available, onRequest }: AvailablePromptsSectionProps) {
  const [showAll, setShowAll] = useState(false);

  return (
    <section className="catalog-section">
      <div className="catalog-section-heading">
        <h2 className="catalog-section-title">
          What you could use <span className="catalog-section-count">({available.length})</span>
        </h2>
        <p className="catalog-section-helper">
          Installed in your workspace but not assigned to you. Ask your admin for access.
        </p>
        {available.length > 6 && (
          <button type="button" className="btn-link" onClick={() => setShowAll((v) => !v)} style={{ fontSize: 12, marginTop: 4 }}>
            {showAll ? 'Show fewer' : `Show all (${available.length})`}
          </button>
        )}
      </div>
      {available.length === 0 ? (
        // Push 5-F (2026-07-11): migrate legacy `cta` -> structured `secondaryAction`.
        <EmptyState
          kind="prompts"
          title="Nothing else is installed in your workspace"
          body="Browse the market to suggest a new prompt for your admin to install."
          secondaryAction={{ label: 'Browse the market', to: '/market?tab=prompts' }}
        />
      ) : (
        <div className="tile-grid">
          {(showAll ? available : available.slice(0, 6)).map((t) => (
            <div key={t.id} className="tile tile-static">
              <div className="tile-header">
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Push #22 rule 3: category-color badge instead of
                      grey tile-meta text. Same scan vocabulary as the
                      assigned list above. */}
                  {t.category !== null && t.category !== '' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <PromptCategoryBadge category={t.category} size="xs" />
                    </div>
                  )}
                  <p className="tile-name">{t.display_name}</p>
                </div>
              </div>
              {t.description !== null && <p className="tile-tagline">{t.description}</p>}
              <div className="tile-footer">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => onRequest(t.name)}
                >
                  Request access
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
